<template>
  <component
    :is="tag"
    class="badge"
    :class="[resolvedClass, { textWrap: wrap, textTruncate: truncate, '-custom': hexColor, '-compact': compact }]"
    :style="badgeStyle"
  >
    <span v-if="!icon && dot" class="badge__dot" :class="resolvedClass"></span>
    <FontAwesomeIcon v-else-if="icon" :icon="'fa-light fa-' + icon" :class="{ 'me-1': text }" aria-hidden="true" />
    <span v-if="truncate" ref="badgeText" class="badge__text">{{ text ?? "" }}</span>
    <template v-else>{{ text ?? "" }}</template>
    <span v-if="baseline" class="fw-light ms-2">{{ baseline }}</span>
    <slot />
    <button
      v-if="removable"
      type="button"
      class="badge__remove"
      :aria-label="removeLabel || $t('remove')"
      :disabled="removeDisabled"
      @click="$emit('remove')"
    >
      <FontAwesomeIcon icon="fa-light fa-xmark" aria-hidden="true" />
    </button>
  </component>
</template>

<script>
import { colorManager } from "@/services/platform/color-manager";

const LIGHT_BACKDROP = "#ffffff";
const CUSTOM_ALPHA = 0.2;

export default {
  name: "BadgeComponent",
  props: {
    icon: {
      type: String,
      required: false,
      default: "",
    },
    text: {
      type: String,
      required: false,
      default: "",
    },
    dot: {
      type: Boolean,
      required: false,
      default: true,
    },
    className: {
      type: String,
      required: false,
      default: "",
    },
    baseline: {
      type: String,
      required: false,
      default: "",
    },
    wrap: {
      type: Boolean,
      required: false,
      default: false,
    },
    truncate: {
      type: Boolean,
      required: false,
      default: false,
    },
    compact: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Un badge inséré dans un <button> doit rester du contenu phrasing : `tag="span"`.
    tag: {
      type: String,
      required: false,
      default: "div",
    },
    removable: {
      type: Boolean,
      required: false,
      default: false,
    },
    removeLabel: {
      type: String,
      required: false,
      default: "",
    },
    removeDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["buttonClick", "remove"],
  computed: {
    // Le className peut porter un nom de classe de statut (success, danger…) ou une couleur hex venant
    // de la base. Sans "#", seul un hex à 6 caractères contenant un chiffre est retenu, pour ne pas
    // lire "add", "bad" ou "decade" comme une couleur
    hexColor() {
      const name = String(this.className ?? "").trim();
      return name.startsWith("#") || /^(?=.*\d)[0-9a-f]{6}$/i.test(name) ? colorManager.toHex(name) : null;
    },
    resolvedClass() {
      return this.hexColor ? "" : this.className ? this.className : "primary";
    },
    badgeStyle() {
      if (!this.hexColor) {
        return null;
      }
      return {
        "--badge-color": this.hexColor,
        "--badge-text": colorManager.readableTextColor(this.hexColor, LIGHT_BACKDROP, CUSTOM_ALPHA),
        backgroundColor: colorManager.hexWithAlpha(this.hexColor, CUSTOM_ALPHA),
      };
    },
  },
  methods: {
    click() {
      this.$emit("buttonClick");
    },
    isTruncated() {
      const el = this.$refs.badgeText;
      if (!el) {
        return false;
      }
      return el.scrollWidth > el.clientWidth;
    },
  },
};
</script>

<style lang="scss" scoped>
$badge-dark-green: darken($green, 28%);
$badge-dark-green-dark-theme: #24614a;

@mixin badge-dark-contrast {
  border-color: $m-color_3;
  background-color: rgba($white, 0.08);
  color: $dark-text;

  .badge__dot {
    background-color: $white;
  }
}

.badge {
  cursor: default;
  width: fit-content;
  // Harmonise la hauteur des badges icône seule (sans text) avec ceux qui ont du text,
  // sinon ils apparaissent plus petits.
  min-height: 30px;
  border: 1px solid $m-color_3;
  background-color: rgba(224, 230, 237, 0.2);
  border-radius: 6px;
  padding: 5px 10px;
  font-weight: 600;
  max-width: 100%;
  display: flex;
  align-items: center;

  &.-compact {
    min-height: 0;
    padding: 1px 6px;
  }

  &.-custom {
    border-color: var(--badge-text);
    color: var(--badge-text);

    @include dark-theme {
      border-color: var(--badge-color);
      color: $dark-text;
    }
  }

  &__remove {
    display: inline-flex;
    align-items: center;
    margin-left: 6px;
    padding: 2px;
    border: none;
    border-radius: 3px;
    background: transparent;
    color: inherit;
    line-height: 1;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      background-color: rgba(0, 0, 0, 0.1);
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 1px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }

    @include dark-theme {
      &:hover,
      &:focus-visible {
        background-color: rgba(255, 255, 255, 0.15);
      }
    }
  }

  &__dot {
    display: inline-block;
    min-width: 10px;
    min-height: 10px;
    max-width: 10px;
    max-height: 10px;
    border-radius: 10px;
    // --badge-color n'est défini que lorsqu'une couleur hex est fournie (cf. badgeStyle)
    background-color: var(--badge-color, #{$m-color_3});
    margin-right: 5px;

    @include dark-theme {
      border: 1px solid rgba(255, 255, 255, 0.3);
    }
  }

  &.success {
    border-color: $green;
    background-color: rgba(106, 199, 164, 0.2);
    color: $green;

    @include dark-theme {
      color: $dark-text;
    }

    .badge__dot {
      background-color: $green;

      @include dark-theme {
        background-color: $white;
      }
    }
  }
  &.light-success {
    border-color: lighten($green, 10%);
    background-color: rgba(106, 199, 164, 0.05);
    color: $green;

    .badge__dot {
      background-color: lighten($green, 10%);
    }
  }

  // Vert foncé : réservé aux statuts définitifs. L'assombrissement et l'alpha 0.25 (au lieu du 0.2
  // des autres variantes) sont calibrés pour tenir le 4.5:1 du RGAA sur fond clair.
  &.dark-green {
    border-color: $badge-dark-green;
    background-color: rgba($green, 0.25);
    color: $badge-dark-green;

    .badge__dot {
      background-color: $badge-dark-green;
    }

    // En thème dark, un texte vert foncé sur fond translucide passe sous le seuil de contraste :
    // on inverse en fond plein vert foncé + texte $dark-text (fond assez sombre pour tenir 4.5:1).
    @include dark-theme {
      border-color: darken($green, 20%);
      background-color: $badge-dark-green-dark-theme;
      color: $dark-text;

      .badge__dot {
        background-color: $white;
      }
    }
  }

  &.warning {
    border-color: $orange;
    background-color: rgba(248, 160, 7, 0.2);
    color: $orange;

    @include dark-theme {
      color: $dark-text;
    }

    .badge__dot {
      background-color: $orange;

      @include dark-theme {
        background-color: $white;
      }
    }
  }

  &.danger {
    border-color: $red;
    background-color: rgba(218, 81, 100, 0.2);
    color: $red;

    @include dark-theme {
      color: $dark-text;
    }

    .badge__dot {
      background-color: $red;

      @include dark-theme {
        background-color: $white;
      }
    }
  }

  &.info {
    border-color: $info;
    background-color: #e7f7ff;
    color: $info;

    .badge__dot {
      background-color: $info;
    }

    @include dark-theme {
      background-color: rgba(0, 150, 200, 0.15);
      color: lighten($info, 20%) !important;
    }
  }

  &.primary {
    border-color: $primary;
    background-color: rgba(13, 110, 253, 0.2);
    color: $primary;
    @include dark-theme {
      color: $dark-text;
    }
    .badge__dot {
      background-color: $primary;

      @include dark-theme {
        background-color: $white;
      }
    }
  }

  // "dark" (ex. GroupCandidacyStatusEnum::toTagClassName() côté back, statut FINALIZED) réutilise
  // le même rendu que "dark-grey", déjà présent avant TA-4812.
  &.dark-grey,
  &.dark {
    border-color: $darker-grey;
    background-color: rgba(29, 29, 29, 0.2);
    color: $darker-grey;

    .badge__dot {
      background-color: $darker-grey;
    }
    // Sans cette surcharge, le texte reste sur un fond sombre translucide en thème dark : contraste
    // insuffisant (comme les variantes success/warning/danger, qui passent au blanc). Partagée par
    // .dark-grey et .candidacy-inactive, dont le rendu dark-theme est identique.
    @include dark-theme {
      @include badge-dark-contrast;
    }
  }
  // GroupCandidacyStatusEnum::toTagClassName() (Placement, statuts CLOSED/DISABLED) - nom
  // sémantique dédié plutôt qu'un nom générique type "secondary", pour ne pas affecter tout autre
  // badge de l'application qui utiliserait cette classe.
  &.candidacy-inactive {
    border-color: $dark-grey;
    background-color: rgba($dark-grey, 0.15);
    color: $dark-grey;

    .badge__dot {
      background-color: $dark-grey;
    }

    @include dark-theme {
      @include badge-dark-contrast;
    }
  }

  // Variante neutre générique (ex. niveau d'une formation dans TrainingDetailModalComponent,
  // correspondance d'origine Yparéo) - même rendu clair que .candidacy-inactive, mais un
  // dark-theme propre (contraste explicite plutôt que badge-dark-contrast), car ces usages ne
  // partagent pas le même fond de carte que les statuts de candidature.
  &.secondary {
    border-color: $dark-grey;
    background-color: rgba($dark-grey, 0.15);
    color: $dark-grey;

    .badge__dot {
      background-color: $dark-grey;
    }

    @include dark-theme {
      border-color: $m-color_3;
      background-color: rgba($white, 0.08);
      color: $white;

      .badge__dot {
        background-color: $white;
      }
    }
  }

  &.ia {
    border-color: $ia-color;
    background-color: $ia-color-bg;
    color: $ia-color;

    .badge__dot {
      background-color: $ia-color;
    }

    @include dark-theme {
      background-color: rgba(161, 124, 247, 0.15);
      color: lighten($ia-color, 15%) !important;
    }
  }

  &.purple {
    border-color: #7c3aed;
    background-color: rgba(124, 58, 237, 0.1);
    color: #7c3aed;

    .badge__dot {
      background-color: #7c3aed;
    }
  }

  &.textWrap {
    text-wrap: wrap;
    overflow-wrap: normal;
    word-break: normal;
  }

  &.textTruncate {
    overflow: hidden;

    svg {
      flex-shrink: 0;
    }
  }

  &__text {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
  }

  &.bg-info {
    border-color: $info;
  }
}
</style>
